/** Map markings — how heavily the map's own markings are drawn: label
 * sizes, label halos, road/trail line widths and their casings, and the
 * app-drawn overlay labels and marker art. A rider's setting, persisted
 * per browser (localStorage key "map.markings"), NOT per trip and NOT
 * carried in share links — the same shape plan/routeLook.ts uses for the
 * route line's Colour/Weight.
 *
 * Why this exists: on the Sep 16 2026 Bodgers shakedown Lee found the map
 * hard to read while bumping down a two-track — "the wording needs to be
 * bigger and our roads and everything need to be more visible against the
 * map backdrop." The style template sizes its labels at 10-12 pt for a
 * phone held still at a desk; on a mount at arm's length over satellite
 * imagery that is too small, and the Satellite look only ever widened
 * halos, never text.
 *
 * THE MULTIPLIERS ARE NOT THIS FILE'S TO CHOOSE.
 * ios/Dustlark/Support/MapMarkings.swift is the source of truth for every
 * number, label, cacheSuffix and flag below; tables/markings.json is a
 * transcription of it, the same way tables/night-map.json transcribes
 * FirstLightStyle's nightMap. tests/map-markings.test.ts pins all of them
 * against the Swift literals, exactly as tests/plan-routelook.test.ts pins
 * the six RouteColor hexes — that test exists because four of those six
 * shipped different on the two platforms and every test still passed,
 * because each side read its own table back to itself.
 *
 * `standard` must leave the generated style byte-identical to what shipped
 * before this landed — nobody who has not touched the control sees a
 * change (applyMarkings in firstlight-looks.ts returns before touching a
 * single layer, and tests/map-markings.test.ts asserts the whole style
 * JSON is identical to a build with no `markings` option at all).
 *
 * Pure and node-safe: no DOM/map types imported here (only `localStorage`
 * and `dispatchEvent`, both guarded the same way plan/routeLook.ts guards
 * them), so this module is testable without a browser and safe to import
 * from firstlight-looks.ts (style generation), RouteOverlay.ts (overlay
 * paint) and LookMenu.tsx (the picker UI) alike. */

import TABLE from "./tables/markings.json";

export type MapMarkings = "standard" | "bold" | "highContrast";

/** One theme's multipliers — the cross-platform contract, field for field
 * the same as MapMarkings.swift's computed properties. */
export interface MarkingsTheme {
  /** Row label in the picker. */
  label: string;
  /** One line under the picker row, in the rider's terms. */
  blurb: string;
  /** Suffix the PHONE folds into its generated style's cache filename.
   * The web planner builds its style fresh in memory on every look change
   * and caches nothing to disk, so nothing here reads it — it is carried
   * because it is part of the contract the two platforms pin together, and
   * dropping it from the table is exactly the kind of quiet divergence
   * tests/map-markings.test.ts exists to catch. */
  cacheSuffix: string;
  /** Hero labels: route numbers, trail names, street names, places, peaks,
   * POIs, water names. */
  textScale: number;
  /** Reference labels: ownership, geology, PLSS sections, contour values —
   * they should grow, but stay behind the hero labels. */
  referenceTextScale: number;
  /** Label halo widths (already widened per look by the Satellite table in
   * firstlight-looks.ts; this multiplies ON TOP of that). */
  haloScale: number;
  /** The legal network and the app-injected trail lines: mvum-*, blm-*,
   * hiking-line, and each one's casing (casing and fill scale together so
   * the outline stays an outline, never a halo). */
  heroLineScale: number;
  /** Basemap roads and paths (road-minor/-secondary/-major, path) and
   * their casings. */
  baseLineScale: number;
  /** App-drawn marker art: diameter scale. The planner draws less than the
   * phone does, so this reaches only the route overlay's waypoint dots and
   * day-break dots (`RouteOverlay.ts`, `applyOverlayMarkings`) — their
   * labels go by textScale/haloScale. The phone's markerScale also covers
   * corridor pins, scout markers and rig crumbs, none of which the planner
   * has. Draw-mode control points, the ghost, the magnetize flash and the
   * hover-scrub cursor are pointer targets sized for the hand, and are
   * deliberately left alone here too. */
  markerScale: number;
  /** Hero labels switch from Noto-Sans-Regular to Noto-Sans-Bold. Only the
   * three bundled faces (Regular/Bold/Italic) are in the glyph stack; any
   * other font name draws nothing, silently. */
  boldLabelFont: boolean;
  /** Whether the theme also recolours the legal network for contrast over
   * imagery (the hexes live in the same table). */
  recoloursNetwork: boolean;
}

interface MarkingsTable {
  key: string;
  fallback: MapMarkings;
  order: MapMarkings[];
  themes: Record<MapMarkings, MarkingsTheme>;
  highContrastColors: Record<string, string>;
  casingLight: string;
  casingImagery: string;
  colourLooks: string[];
  referenceLineOpacityScale: number;
  boldLabelFontStack: string[];
}

export const MARKINGS_TABLE = TABLE as MarkingsTable;

export const MARKINGS: Record<MapMarkings, MarkingsTheme> = MARKINGS_TABLE.themes;

/** Canonical UI order — the order MapMarkings.allCases yields on the phone
 * and the order LookMenu renders the rows. An explicit array keeps that a
 * guarantee rather than an incidental JS key-insertion detail (same
 * reasoning as routeLook.ts's ROUTE_COLOR_ORDER). */
export const MARKINGS_ORDER: readonly MapMarkings[] = MARKINGS_TABLE.order;

export const DEFAULT_MARKINGS: MapMarkings = MARKINGS_TABLE.fallback;

/** Same key string the phone writes into UserDefaults — not because the
 * two stores ever meet, but because one name for one setting is how the
 * contract stays readable from either side. */
const STORAGE_KEY = MARKINGS_TABLE.key;

/** Dispatched on `window` whenever `saveMapMarkings` writes a new theme —
 * the web analog of MapMarkings.didChange. Like the phone, the map answers
 * this with a full style rebuild rather than a live repaint: the widths and
 * sizes live in zoom-interpolated expressions baked into the generated
 * style, not in properties a setPaintProperty could move. A deliberate
 * menu pick can afford the rebuild. */
export const MAP_MARKINGS_CHANGED = "dustlark:map-markings";

// In-memory fallback for a write that misses localStorage (quota, private
// mode, or no localStorage at all) — same degrade-to-memory shape as
// plan/routeLook.ts, scoped to this one key.
let memory: string | null = null;
let degraded = false;

function getStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

export function isMapMarkings(v: unknown): v is MapMarkings {
  return typeof v === "string" && Object.prototype.hasOwnProperty.call(MARKINGS, v);
}

/** Reads the saved theme. Never throws: a missing/absent localStorage, an
 * unrecognized value (an older schema, a hand-edited value, plain
 * corruption) or no value at all all land on `standard` — the theme that
 * leaves the map exactly as it shipped. Stored as the bare raw string, the
 * same spelling the phone's UserDefaults holds. */
export function loadMapMarkings(): MapMarkings {
  const ls = getStorage();
  let raw: string | null = null;
  if (ls && !degraded) {
    try {
      raw = ls.getItem(STORAGE_KEY);
    } catch {
      degraded = true;
    }
  }
  if (raw === null) raw = memory;
  return isMapMarkings(raw) ? raw : DEFAULT_MARKINGS;
}

/** Writes the theme and announces it (MAP_MARKINGS_CHANGED), degrading to
 * the in-memory fallback on quota/private-mode rejection so the session
 * keeps working even though the choice won't survive a reload. */
export function saveMapMarkings(markings: MapMarkings): void {
  const ls = getStorage();
  let written = false;
  if (ls) {
    try {
      ls.setItem(STORAGE_KEY, markings);
      degraded = false;
      memory = null;
      written = true;
    } catch {
      // Quota exceeded or private-mode write rejection — degrade below.
    }
  }
  if (!written) {
    degraded = true;
    memory = markings;
  }
  try {
    if (typeof dispatchEvent === "function") {
      dispatchEvent(new CustomEvent(MAP_MARKINGS_CHANGED, { detail: markings }));
    }
  } catch {
    // No DOM (node tests) — the write above still stands.
  }
}
